import React, { useState, useEffect } from 'react';
import {
    Input,
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Spinner,
    Link,
    SharedSelection,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
} from '@heroui/react';
import {
    ExclamationCircleIcon,
    ArrowRightIcon,
    ArrowUpRightIcon,
    EyeIcon,
    EyeSlashIcon,
    GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RegisterFormData } from '../../types/types';
import hoowlrLogo from '../../assets/images/logo.svg';
import loginBanner from '../../assets/images/login-banner.png';

const authInputClassNames = {
    base: 'gap-1',
    label:
        '!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0',
    inputWrapper:
        'h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]',
    input: 'text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]',
    errorMessage: 'text-left ml-1 w-full text-xs',
};

const Register: React.FC = () => {
    const { t, i18n } = useTranslation('register');
    const [formData, setFormData] = useState<RegisterFormData>({
        email: '',
        password: '',
        phone: '',
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [navigate, setNavigate] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(localStorage.getItem('i18nextLng') || 'en');
    const { isOpen, onOpen, onClose } = useDisclosure();

    const languageNames: { [key: string]: string } = {
        en: t('language_en'),
        de: t('language_de'),
        hr: t('language_hr'),
    };

    const handleLanguageChange = (keys: SharedSelection) => {
        let value: string;
        if (typeof keys === 'string') {
            value = keys;
        } else if ('currentKey' in keys && typeof keys.currentKey === 'string') {
            value = keys.currentKey;
        } else {
            value = 'en';
        }
        setSelectedLanguage(value);
        i18n.changeLanguage(value);
        localStorage.setItem('i18nextLng', value);
    };

    useEffect(() => {
        const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
        i18n.changeLanguage(savedLanguage);
    }, [i18n]);

    const togglePasswordVisibility = (): void => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    const handleEmailChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, email: value }));
        setErrors((prev) => ({ ...prev, email: '' }));
    };

    const handlePhoneChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, phone: value }));
        setErrors((prev) => ({ ...prev, phone: '' }));
    };

    const handlePasswordChange = (value: string): void => {
        setFormData((prev) => ({ ...prev, password: value }));
        setErrors((prev) => ({ ...prev, password: '' }));
    };

    const validateEmail = (value: string): string | null => {
        if (!value) {
            return t('email_required');
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return t('email_invalid');
        }
        return null;
    };

    const validatePhone = (value: string): string | null => {
        if (!value.trim()) {
            return null;
        }
        if (!/^\+?\d{7,15}$/.test(value)) {
            return t('phone_invalid');
        }
        return null;
    };

    const validatePassword = (value: string): string | null => {
        const passwordErrors = [];
        if (!value) {
            return t('password_required');
        }
        if (value.length < 8) {
            passwordErrors.push(t('password_min_length'));
        }
        if (!/[A-Z]/.test(value)) {
            passwordErrors.push(t('password_uppercase'));
        }
        if (!/[a-z]/.test(value)) {
            passwordErrors.push(t('password_lowercase'));
        }
        if (!/[!@#$%^&*]/.test(value)) {
            passwordErrors.push(t('password_special'));
        }
        return passwordErrors.length > 0 ? passwordErrors.join(', ') : null;
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        const emailError = validateEmail(formData.email);
        const phoneError = validatePhone(formData.phone);
        const passwordError = validatePassword(formData.password);

        if (emailError || phoneError || passwordError) {
            setErrors({
                email: emailError || '',
                phone: phoneError || '',
                password: passwordError || '',
            });
            return;
        }

        setLoading(true);
        setError(null);
        try {
            setTimeout(() => {
                setLoading(false);
                setNavigate(true);
            }, 1000);
        } catch (err) {
            setError(t('error_message'));
            setLoading(false);
            onOpen();
        }
    };

    const closeErrorPopup = (): void => {
        setError(null);
        onClose();
    };

    useEffect(() => {
        if (error) {
            onOpen();
        }
    }, [error, onOpen]);

    if (navigate) {
        return <Navigate to="/registration-setup" state={formData} replace />;
    }

    return (
        <div className="register-page flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
            {/* LEFT COLUMN */}
            <div className="flex min-w-0 flex-1 flex-col">
                <div
                    className="
                        flex min-h-dvh flex-col justify-between
                        px-6 py-6 sm:px-12 sm:py-8
                        lg:py-9
                        [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
                        [@media(max-height:700px)]:lg:py-3
                    "
                >
                    {/* Header: logo + language dropdown */}
                    <div className="flex w-full items-center justify-between">
                        <img
                            src={hoowlrLogo}
                            alt="Hoowlr"
                            className="
                                h-9 w-auto object-contain object-left
                                lg:h-16 lg:w-48
                                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                                [@media(max-height:700px)]:lg:h-10
                            "
                        />
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="h-8 lg:h-9 gap-1.5 text-xs lg:text-sm font-medium text-[#71717A] font-['Plus_Jakarta_Sans',sans-serif] rounded-xl border border-slate-200/80 lg:border-transparent"
                                    startContent={<GlobeAltIcon className="h-4 w-4 lg:h-5 lg:w-5 text-slate-500" />}
                                    aria-label={t('language_select_label')}
                                >
                                    {languageNames[selectedLanguage] || 'English'}
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                itemClasses={{
                                base: "rounded-xl data-[focus-visible=true]:outline-0 data-[focus-visible=true]:ring-0 data-[focus-visible=true]:ring-offset-0 data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:bg-[#EBEBEC]",
                            }}
                                aria-label="Language selection"
                                onSelectionChange={handleLanguageChange}
                                selectionMode="single"
                                selectedKeys={[selectedLanguage]}
                            >
                                <DropdownItem key="en">{t('language_en')}</DropdownItem>
                                <DropdownItem key="de">{t('language_de')}</DropdownItem>
                                <DropdownItem key="hr">{t('language_hr')}</DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>

                    {/* Centered form container */}
                    <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
                        <h1
                            className="
                                text-center text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B] mb-3 sm:mb-4 lg:mb-8
                                [@media(max-height:700px)]:mb-2
                            "
                        >
                            {t('welcome_title')}
                        </h1>

                        <div
                            className="
                                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
                            "
                        >
                            <div className="flex flex-col gap-1 sm:gap-2 [@media(max-height:700px)]:gap-0.5 text-left">
                                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold leading-6 lg:leading-8 text-[#18181B]">{t('create_account_title')}</h2>
                                <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">{t('create_account_subtitle')}</p>
                            </div>

                            <form
                                noValidate
                                className="
                                    flex flex-col gap-4 lg:gap-6
                                    [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                                    [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
                                "
                                onSubmit={handleSubmit}
                            >
                                <div
                                    className="
                                        flex flex-col gap-3 lg:gap-4
                                        [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                                    "
                                >
                                    <Input
                                        type="email"
                                        name="email"
                                        label={t('email_label')}
                                        labelPlacement="outside"
                                        placeholder={t('email_placeholder')}
                                        value={formData.email}
                                        onValueChange={handleEmailChange}
                                        className="w-full"
                                        classNames={authInputClassNames}
                                        size="md"
                                        isRequired
                                        validate={validateEmail}
                                        isInvalid={!!errors.email}
                                        errorMessage={errors.email}
                                    />

                                    <Input
                                        type="tel"
                                        name="phone"
                                        label={t('phone_label')}
                                        labelPlacement="outside"
                                        placeholder={t('phone_placeholder')}
                                        value={formData.phone}
                                        onValueChange={handlePhoneChange}
                                        className="w-full"
                                        classNames={authInputClassNames}
                                        size="md"
                                        validate={validatePhone}
                                        isInvalid={!!errors.phone}
                                        errorMessage={errors.phone}
                                    />

                                    <Input
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        name="password"
                                        label={t('password_label')}
                                        labelPlacement="outside"
                                        placeholder={t('password_placeholder')}
                                        value={formData.password}
                                        onValueChange={handlePasswordChange}
                                        className="w-full"
                                        classNames={authInputClassNames}
                                        endContent={
                                            <button
                                                className="focus:outline-none"
                                                type="button"
                                                onClick={togglePasswordVisibility}
                                                aria-label="toggle password visibility"
                                            >
                                                {isPasswordVisible ? (
                                                    <EyeSlashIcon className="h-4 w-4 text-gray-400 pointer-events-none" />
                                                ) : (
                                                    <EyeIcon className="h-4 w-4 text-gray-400 pointer-events-none" />
                                                )}
                                            </button>
                                        }
                                        size="md"
                                        isRequired
                                        validate={validatePassword}
                                        isInvalid={!!errors.password}
                                        errorMessage={errors.password}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    isLoading={loading}
                                    disabled={loading}
                                    endContent={loading ? null : <ArrowRightIcon className="h-4 w-4" />}
                                    spinner={<Spinner size="sm" color="white" />}
                                    className="h-10 lg:h-10 w-full rounded-2xl lg:rounded-3xl bg-[#0485F7] text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm"
                                >
                                    {loading ? '' : t('continue_button')}
                                </Button>
                            </form>

                            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-2 text-xs sm:text-sm lg:text-base leading-5 lg:leading-6">
                                <span className="text-[#71717A]">{t('already_account')}</span>
                                <Link
                                    href="/login"
                                    className="gap-0.5 px-0.5 text-xs sm:text-sm lg:text-base text-[#2563EB] font-semibold lg:font-normal"
                                    underline="hover"
                                >
                                    {t('sign_in_link')}
                                    <ArrowUpRightIcon className="mt-0.5 h-2.5 w-2.5 opacity-60" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <p
                        className="
                            pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]
                        "
                    >
                        {t('footer_copyright', '© Hoowlr 2026, All rights reserved')}
                    </p>
                </div>
            </div>

            {/* RIGHT BANNER (hidden below lg) */}
            <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
                <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
                    <img src={loginBanner} alt="" className="max-h-full max-w-full object-contain" />
                </div>
            </div>

            <Modal isOpen={isOpen} onClose={closeErrorPopup} size="sm" placement="center">
                <ModalContent>
                    <ModalHeader className="flex items-center gap-2">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-50">
                            <ExclamationCircleIcon className="h-4 w-4 text-red-500" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-800">{t('error_title')}</h3>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm leading-relaxed text-gray-600">{t('error_message')}</p>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="danger"
                            variant="solid"
                            onPress={closeErrorPopup}
                            size="sm"
                            className="w-full"
                        >
                            {t('error_close_button')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};

export default Register;